import { Rng, hashSeed } from "@enjoyment/core";
import { createGame, finishReveal, placeCard, type ZamanState } from "./game";
import { difficultyLevel, type ZamanLevel } from "./generator";

export type DifficultyBand = "easy" | "medium" | "hard" | "expert" | "broken";

export interface DifficultyGrade {
  level: number;
  winRate: number;
  threeStarRate: number;
  averageMistakes: number;
  band: DifficultyBand;
}

function playSlots(level: ZamanLevel, choose: (state: ZamanState, rng: Rng) => number, seed: number): ZamanState {
  const rng = new Rng(seed);
  let state = createGame(level);
  while (state.phase === "placing") {
    const slot = choose(state, rng);
    const placed = placeCard(state, slot);
    if (placed === state) break;
    state = finishReveal(placed);
  }
  return state;
}

/** A perfect historian: uses the exact chronology for every card. */
export function oracleBot(level: ZamanLevel): ZamanState {
  return playSlots(level, (state) => state.correctSlot, hashSeed(`zaman:oracle:${level.seed}`));
}

/** Per-card recall noise rises with event tier and numbered-level difficulty. */
export function fuzzyNoise(eventTier: 1 | 2 | 3, levelNumber: number): number {
  const levelPressure = Math.min(0.78, Math.max(0, levelNumber - 1) * 0.018);
  return Math.min(0.6, 0.012 + (eventTier - 1) * 0.105 + levelPressure);
}

/**
 * A seeded player model: sometimes recalls an adjacent/nearby insertion instead of exact history. The level
 * pressure is the level's difficulty: its number up to 20, the chapter design's `effective` ramp level from 21.
 */
export function fuzzyBot(level: ZamanLevel, trialSeed = 0): ZamanState {
  const pressureLevel = difficultyLevel(level.n);
  return playSlots(
    level,
    (state, rng) => {
      if (!state.current || rng.next() >= fuzzyNoise(state.current.tier, pressureLevel)) return state.correctSlot;
      const maxJump = Math.max(1, Math.min(state.timeline.length, 1 + Math.floor((pressureLevel - 1) / 7) + state.current.tier - 1));
      const jump = 1 + rng.int(maxJump);
      const direction = rng.int(2) === 0 ? -1 : 1;
      let guess = Math.max(0, Math.min(state.timeline.length, state.correctSlot + direction * jump));
      if (state.timeline.length > 0) {
        const accepted = new Set<number>();
        let i = 0;
        while (i < state.timeline.length && state.timeline[i]!.year < state.current.year) i++;
        let end = i;
        while (end < state.timeline.length && state.timeline[end]!.year === state.current.year) end++;
        for (let slot = i; slot <= end; slot++) accepted.add(slot);
        if (accepted.has(guess)) {
          const alternatives = Array.from({ length: state.timeline.length + 1 }, (_, slot) => slot).filter((slot) => !accepted.has(slot));
          if (alternatives.length) guess = alternatives[rng.int(alternatives.length)]!;
        }
      }
      return guess;
    },
    hashSeed(`zaman:fuzzy:${level.seed}:${trialSeed}`),
  );
}

export function bandOf(winRate: number): DifficultyBand {
  if (winRate >= 0.95) return "easy";
  if (winRate >= 0.8) return "medium";
  if (winRate >= 0.5) return "hard";
  if (winRate >= 0.3) return "expert";
  return "broken";
}

/** Monte Carlo with deterministic trials; suited to regression tests and content tuning. */
export function gradeLevel(level: ZamanLevel, trials = 200): DifficultyGrade {
  let wins = 0;
  let threeStars = 0;
  let mistakes = 0;
  for (let trial = 0; trial < trials; trial++) {
    const result = fuzzyBot(level, trial);
    if (result.phase === "won") wins++;
    if (result.stars === 3) threeStars++;
    mistakes += result.mistakes;
  }
  const winRate = wins / trials;
  return { level: level.n, winRate, threeStarRate: threeStars / trials, averageMistakes: mistakes / trials, band: bandOf(winRate) };
}

export function gradeLevels(levels = 30, trials = 200): DifficultyGrade[] {
  return Array.from({ length: levels }, (_, index) => gradeLevel((requireLevel(index + 1)), trials));
}

// Kept local to avoid a circular dependency in callers that import only the bot.
function requireLevel(n: number): ZamanLevel {
  // Static import is intentional in the pure core; no runtime services are used.
  return generateLevelLocal(n);
}

import { generateLevel as generateLevelLocal } from "./generator";
